import {Directive, inject} from '@angular/core';
import {PlayersService} from "../../services/players.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {IPlayerPaymentMethodQueryResult} from "../../interfaces/player/IPlayerPaymentMethodQueryResult";
import {FormControl, FormGroup} from "@angular/forms";
import {IPlayerPaymentMethodFilter} from "../../interfaces/player/IPlayerPaymentMethodFilter";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";

@Directive()
export abstract class PaymentMethodsBaseComponent {
  private playerService: PlayersService = inject(PlayersService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);

  protected uiState: UIStateEnum = UIStateEnum.ShowData;
  protected uiStateForTemplate = UIStateEnum;
  protected paymentMethods: IPlayerPaymentMethodQueryResult[] = [];
  protected paymentMethodsGridApi!: GridApi<IPlayerPaymentMethodQueryResult>;
  protected game: IGameQueryResult | null = null;
  protected isCrossGame = false;

  protected filterForm = new FormGroup({
    playerId: new FormControl(''),
    paymentMethodId: new FormControl(''),
    cardFingerprint: new FormControl(''),
    cardLastFourDigits: new FormControl(''),
    playerEmail: new FormControl('')
  });

  protected columnDefs: ColDef[] = [
    {
      headerName: 'Player Email',
      field: 'PlayerEmail',
      resizable: true,
      sortable: true,
      filter: true,
      width: 250
    },
    {
      headerName: 'Payment Method ID',
      field: 'Id',
      resizable: true,
      sortable: true,
      width: 250
    },
    {
      headerName: 'Player ID',
      field: 'PlayerId',
      resizable: true,
      sortable: true,
      width: 250
    },
    {
      headerName: 'Last 4 Digits',
      field: 'CardLastFourDigits',
      resizable: true,
      sortable: true,
      width: 120
    },
    {
      headerName: 'Exp Month',
      field: 'CardExpirationMonth',
      resizable: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'Exp Year',
      field: 'CardExpirationYear',
      resizable: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'Card Fingerprint',
      field: 'CardFingerprint',
      resizable: true,
      sortable: true,
      width: 200
    },
    {
      headerName: 'Active',
      field: 'Active',
      resizable: true,
      sortable: true,
      width: 100,
      cellRenderer: (params: { value: boolean }) => params.value ? 'Yes' : 'No'
    },
    {
      headerName: 'Deactivated On',
      field: 'DeactivatedOn',
      resizable: true,
      sortable: true,
      width: 180,
      valueFormatter: (params: { value: string | null }) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString();
      }
    }
  ];

  onGameChosen(chosenGameP: IGameQueryResult): void {
    this.game = chosenGameP;
    this.searchPaymentMethods();
  }

  onSearchClick(): void {
    this.searchPaymentMethods();
  }

  onResetClick(): void {
    this.filterForm.reset();
    this.searchPaymentMethods();
  }

  protected searchPaymentMethods(): void {
    if (!this.game) {
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    const filter: IPlayerPaymentMethodFilter = {
      gameId: this.game.Id,
      playerId: this.filterForm.value.playerId || undefined,
      paymentMethodId: this.filterForm.value.paymentMethodId || undefined,
      cardFingerprint: this.filterForm.value.cardFingerprint || undefined,
      cardLastFourDigits: this.filterForm.value.cardLastFourDigits || undefined,
      playerEmail: this.filterForm.value.playerEmail || undefined
    };

    this.playerService.getPaymentMethodsByGame(filter).subscribe({
      next: (paymentMethods) => {
        this.paymentMethods = paymentMethods;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  onGridReady(params: GridReadyEvent<IPlayerPaymentMethodQueryResult>): void {
    this.paymentMethodsGridApi = params.api;
    this.paymentMethodsGridApi.sizeColumnsToFit({
      defaultMinWidth: 100
    });
  }
}
